import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';

import { useAuthUser } from 'auth/hooks';
import { Confirmation } from 'core/components';
import { LocalStorageKeys } from 'core/enums';
import { QuoteEntityType, QuoteVersionType } from 'quotes/enums';
import { Color, Variant } from 'theme/enums';

import { useExpiredCostingContext } from './expiredCosting';

interface IProps {
  quoteId: number;
  quoteEntityType: QuoteEntityType;
  quoteVersionType: QuoteVersionType;
}

interface IQuoteExpiredCostingCanceled {
  user: number;
  quoteId: number;
  expiresAt: number;
}

const EXPIRATION_HOURS = 24;

export const QuotePartExpiredCostingWarning = ({ quoteId, quoteEntityType }: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const [quoteExpiredCostingCanceled, setQuoteExpiredCostingCanceled] = useLocalStorage<
    IQuoteExpiredCostingCanceled[]
  >(LocalStorageKeys.QUOTE_EXPIRED_COSTING_CANCELED, []);

  const { t } = useTranslation();

  const { onResetExpiredCosts } = useExpiredCostingContext();

  const { user } = useAuthUser();

  useEffect(() => {
    if (!quoteExpiredCostingCanceled || !user) {
      setIsOpen(true);

      return;
    }

    const canceledEntry = quoteExpiredCostingCanceled.find(
      (entry) => entry.quoteId === quoteId && entry.user === user.id
    );

    if (!canceledEntry) {
      setIsOpen(true);

      return;
    }

    const now = new Date().getTime();

    if (now < canceledEntry.expiresAt) {
      setIsOpen(false);

      return;
    }

    setQuoteExpiredCostingCanceled(
      quoteExpiredCostingCanceled.filter(
        (entry) => !(entry.quoteId === quoteId && entry.user === user.id)
      )
    );
  }, [quoteExpiredCostingCanceled, quoteId, user, setQuoteExpiredCostingCanceled]);

  const onConfirm = async () => {
    setIsOpen(false);

    onResetExpiredCosts();
  };

  const onCancel = () => {
    setIsOpen(false);

    if (!user) {
      return;
    }

    setQuoteExpiredCostingCanceled([
      ...quoteExpiredCostingCanceled,
      {
        user: user.id,
        quoteId,
        expiresAt: new Date().getTime() + EXPIRATION_HOURS * 60 * 60 * 1000,
      },
    ]);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Confirmation
      isOpen={isOpen}
      color={Color.WARNING}
      variant={Variant.SOFT}
      title={t('titleQuotePartExpiredCostingWarning')}
      message={t('messageConfirmResetQuotePartExpiredCosting', { quoteEntityType })}
      onConfirm={onConfirm}
      onCancel={onCancel}
      confirmBtnLabel={t('buttonLabelConfirmResetQuotePartExpiredCosting')}
      cancelBtnLabel={t('buttonLabelCancelResetQuotePartExpiredCosting', { quoteEntityType })}
    />
  );
};
